import { HttpClient } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ServerSyncDataInterface } from '@shared/api';
import { environment, EnvironmentLocalesEnum } from '@shared/environments';
import { KeycloakService } from 'keycloak-angular';
import { difference, isEqual, union } from 'lodash-es';
import {
  catchError,
  Observable,
  of,
  pipe,
  switchMap,
  takeWhile,
  tap,
  throwError,
  timer,
} from 'rxjs';
import {
  ThemeEnum,
  UserAppDataInterface,
  UserAppDataTableInterface,
  UserStateInterface,
} from './user-data.interface';
import { upgradeUserData } from './user-data.migrations';
import { validateUserData } from './user-data.validators';

export const USER_DATA_SCHEMA_VERSION = 16; //increment when schema is updated. Make sure all validators are still relevant

export const initialState: UserStateInterface = {
  appData: {
    table: {},
    newsReadIds: [],
    schemaVersion: USER_DATA_SCHEMA_VERSION,
    isSidebarClosed: false,
    sidebarOpenedItems: [],
    theme: ThemeEnum.LIGHT,
    locale: environment.defaultLocale ?? EnvironmentLocalesEnum.EN,
  },
  team: { id: null, token: null },
  guid: null,
  name: null,
  email: null,
  siteCount: null,
  nodeCount: null,
  hasLegalConsent: null,
  registered: null,
  transfer: {
    canRun: null,
    isInProgress: null,
  },
  isBlocked: null,
  eaEuAccess: null,
};

export type UserDataStore = InstanceType<typeof UserDataStore>;

export const UserDataStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    isDarkThemeEnabled: computed(
      () => store?.appData?.theme() === ThemeEnum.DARK,
    ),
  })),
  withMethods((store, http = inject(HttpClient)) => ({
    saveTableState(id: string, data: Partial<UserAppDataTableInterface>): void {
      patchState(store, (state) => ({
        ...state,
        appData: {
          ...state.appData,
          table: {
            ...state.appData.table,
            [id]: { ...state.appData.table[id], ...data },
          },
        },
      }));

      this.patchUserData();
    },
    toggleTheme() {
      patchState(store, (state) => ({
        ...state,
        appData: {
          ...state.appData,
          theme:
            state.appData.theme === ThemeEnum.LIGHT
              ? ThemeEnum.DARK
              : ThemeEnum.LIGHT,
        },
      }));

      this.patchUserData();
    },
    saveNewsIds(ids: number[]) {
      if (!isEqual(store.appData.newsReadIds(), ids)) {
        patchState(store, (state) => ({
          ...state,
          appData: {
            ...state.appData,
            newsReadIds: ids,
          },
        }));

        this.patchUserData();
      }
    },
    acceptLegalConsent() {
      http
        .post(`${environment.apiHost}/users/legal-consent-accept`, {})
        .pipe(
          switchMap(() => this.initUserData$()),
          tap(() =>
            patchState(store, (state) => ({
              hasLegalConsent: true,
            })),
          ),
        )
        .subscribe();
    },
    registerUser() {
      http
        .post(`${environment.apiHost}/users/register`, { legalConsent: true })
        .pipe(
          switchMap(() => this.initRegisteredUserData$()),
          tap(() =>
            patchState(store, (state) => ({
              registered: true,
            })),
          ),
        )
        .subscribe();
    },
    toggleSidebar() {
      patchState(store, (state) => ({
        ...state,
        appData: {
          ...state.appData,
          isSidebarClosed: !store.appData.isSidebarClosed(),
        },
      }));

      this.patchUserData();
    },
    addSidebarOpenedItems(itemId: string) {
      patchState(store, (state) => ({
        ...state,
        appData: {
          ...state.appData,
          sidebarOpenedItems: union(store.appData.sidebarOpenedItems(), [
            itemId,
          ]),
        },
      }));

      this.patchUserData();
    },
    removeSidebarOpenedItems(itemId: string) {
      patchState(store, (state) => ({
        ...state,
        appData: {
          ...state.appData,
          sidebarOpenedItems: difference(store.appData.sidebarOpenedItems(), [
            itemId,
          ]),
        },
      }));

      this.patchUserData();
    },
    increaseSitesCountOptimistically(siteCount: number) {
      const newSiteCount = (store.siteCount() ?? 0) + siteCount;

      if (!isEqual(newSiteCount, store.siteCount())) {
        patchState(store, () => ({
          siteCount: (siteCount ?? 0) + siteCount,
        }));
      }
    },
    decreaseSitesCountOptimistically(siteCount: number) {
      const newSiteCount = (store.siteCount() ?? 0) - siteCount;

      if (!isEqual(newSiteCount, store.siteCount())) {
        patchState(store, () => ({
          siteCount: newSiteCount,
        }));
      }
    },
    initRegisteredUserData$(): Observable<
      ServerSyncDataInterface<Required<UserAppDataInterface>>
    > {
      return http
        .get<
          ServerSyncDataInterface<UserAppDataInterface>
        >(`${environment.apiHost}/users/me`)
        .pipe(
          switchMap((data) => {
            if (data.hasLegalConsent) {
              const upgradedData = upgradeUserData(data);
              const validatedData = validateUserData(upgradedData);

              if (data.hasLegalConsent && !isEqual(data, validatedData)) {
                return http.patch<
                  ServerSyncDataInterface<Required<UserAppDataInterface>>
                >(`${environment.apiHost}/users/me`, {
                  appData: {
                    ...validatedData.appData,
                    newsReadIds: environment.news?.map((news) => news.id) ?? [],
                  },
                });
              } else {
                return of(validatedData);
              }
            } else {
              return of(data);
            }
          }),
          tap((data: any) => patchState(store, data)),
        );
    },
    initUserData$(): Observable<
      ServerSyncDataInterface<Required<UserAppDataInterface>>
    > {
      return http
        .get<
          ServerSyncDataInterface<UserAppDataInterface>
        >(`${environment.apiHost}/users/me`)
        .pipe(
          catchError((error) => {
            if (error.status === 451) {
              return of({
                registered: false,
              } as ServerSyncDataInterface<UserAppDataInterface>);
            } else {
              return throwError(() => error);
            }
          }),
          switchMap((data) => {
            if (data.hasLegalConsent) {
              const upgradedData = upgradeUserData(data);
              const validatedData = validateUserData(upgradedData);

              if (data.hasLegalConsent && !isEqual(data, validatedData)) {
                return http.patch<
                  ServerSyncDataInterface<Required<UserAppDataInterface>>
                >(`${environment.apiHost}/users/me`, {
                  appData: {
                    ...validatedData.appData,
                    newsReadIds: environment.news?.map((news) => news.id) ?? [],
                  },
                });
              } else {
                return of(validatedData);
              }
            } else {
              return of(data);
            }
          }),
          tap((data: any) => patchState(store, data)),
        );
    },
    fetchUserData: rxMethod<void>(
      pipe(
        switchMap(() =>
          http
            .get<
              ServerSyncDataInterface<UserAppDataInterface>
            >(`${environment.apiHost}/users/me`)
            .pipe(tap((response: any) => patchState(store, response))),
        ),
      ),
    ),
    patchUserData: rxMethod<void>(
      pipe(
        switchMap(() =>
          http
            .patch<ServerSyncDataInterface<Required<UserAppDataInterface>>>(
              `${environment.apiHost}/users/me`,
              {
                appData: store.appData(),
              },
            )
            .pipe(tap((response: any) => patchState(store, response))),
        ),
      ),
    ),
  })),
  withHooks({
    onInit(store, keycloakService = inject(KeycloakService)) {
      timer(0, environment.loadDataPeriod ?? 15000)
        .pipe(
          takeUntilDestroyed(),
          takeWhile(() => !store.siteCount()),
        )
        .subscribe(() => {
          if (keycloakService.isLoggedIn()) {
            store.fetchUserData();
          }
        });
    },
  }),
);
